import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  DestroyRef,
  HostListener,
  inject,
  OnInit,
  PLATFORM_ID,
} from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { CommonModule, DOCUMENT, isPlatformBrowser } from '@angular/common';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Observable, Subject, of, switchMap, filter, distinctUntilChanged, catchError } from 'rxjs';
import { AuthComponent } from '../auth/auth.component';
import { UserService } from '../../services/user.service';
import { StorageService } from '../../services/storage.service';
import { HeaderCountService } from '../../services/header.service';
import { AuthService } from '../../services/auth.service';
import { LocationService } from '../../services/location.service';
import { AddressService } from '../../services/address.service';
import { CurrentLocation, DeliveryInfo } from '../../types/location.types';
import { AnalyticsService } from '../../services/analytics.service';
import { ShopService } from '../../services/shop.service';
import { SearchService } from '../../services/search.service';
import { SearchBarComponent } from '../../shared/components/search-bar/search-bar.component';
import { ThemeService } from '../../services/theme.service';
import { LocationPickerService } from '../../services/location-picker.service';
import { CartService } from '../../services/cart.service';
import { ChatService } from '../../services/chat.service';
import { ChatInboxService } from '../../services/chat-inbox.service';
import { pollWhileVisible } from '../../core/http/polling';

/** Cart hover preview is refetched at most this often while the user moves the mouse around. */
const CART_PREVIEW_TTL_MS = 15000;

@Component({
  selector: 'app-header',
  standalone: true,
  templateUrl: './header.component.html',
  styleUrls: ['./header.component.scss'],
  imports: [CommonModule, FormsModule, RouterLink, RouterLinkActive, SearchBarComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit {
  readonly router = inject(Router);
  private modalService = inject(NgbModal);
  private userService = inject(UserService);
  private storageService = inject(StorageService);
  private headerService = inject(HeaderCountService);
  private authService = inject(AuthService);
  private locationService = inject(LocationService);
  private analytics = inject(AnalyticsService);
  private shopService = inject(ShopService);
  private searchService = inject(SearchService);
  private cartService = inject(CartService);
  private locationPicker = inject(LocationPickerService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);
  readonly themeService = inject(ThemeService);
  readonly addressService = inject(AddressService);
  readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private chatService = inject(ChatService);
  private document = inject(DOCUMENT);
  readonly chatInbox = inject(ChatInboxService);

  searchQuery = '';
  searchResults: any[] = [];
  popularSearches: string[] = [];
  private searchTerms = new Subject<string>();

  userName = '';
  /** /search has its own full-width search box, so the header's is hidden there. */
  onSearchPage = false;
  showProfileDropdown = false;
  cartCount = 0;
  isLoggedIn$: Observable<boolean> = this.authService.isLoggedIn$;

  // Current delivery location, derived from AddressService.selectedAddress via LocationService
  currentLocation: CurrentLocation | null = null;
  deliveryInfo: DeliveryInfo | null = null;

  // Nearby-shop search radius (independent of which address is selected)
  radius = 1;
  showRadiusPanel = false;

  // Sticky header gets a shadow once the page scrolls
  isScrolled = false;
  private scrollTicking = false;

  // Cart hover preview (desktop only - mouseenter/mouseleave don't fire on touch)
  showCartPreview = false;
  cartPreviewLoading = false;
  cartPreview: { items: any[]; total: number; grand_total?: number } | null = null;
  private cartPreviewFetchedAt = 0;
  private cartPreviewHideTimeout?: ReturnType<typeof setTimeout>;

  ngOnInit(): void {
    this.onSearchPage = this.router.url.startsWith('/search');
    this.router.events.pipe(
      filter((e): e is NavigationEnd => e instanceof NavigationEnd),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(e => {
      this.onSearchPage = e.urlAfterRedirects.startsWith('/search');
      this.cdr.markForCheck();
    });

    if (!this.authService.hasValidToken()) {
      this.authService.logout();
    }

    this.headerService.counts$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(counts => {
      this.cartCount = counts.cart_count;
      this.cartPreviewFetchedAt = 0; // cart changed - next hover shows fresh data
      this.cdr.markForCheck();
    });

    // Show the real account name; cleared on logout.
    this.isLoggedIn$.pipe(
      distinctUntilChanged(),
      switchMap(loggedIn => loggedIn && this.isBrowser
        ? this.userService.getUserInfo().pipe(catchError(() => of(null)))
        : of(null)),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(user => {
      this.userName = user?.username || user?.phone_number || '';
      this.headerService.fetchCounts();
      this.cdr.markForCheck();
    });

    // Unread chat badge: the newest conversations, refreshed while the tab is visible.
    this.isLoggedIn$.pipe(
      distinctUntilChanged(),
      switchMap(loggedIn => {
        if (!loggedIn) this.chatInbox.reset();
        return loggedIn && this.isBrowser
          ? pollWhileVisible(this.document, UNREAD_POLL_MS, () => this.chatService.listConversations(1))
          : of(null);
      }),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(res => {
      if (res) this.chatInbox.mergeLatest(res.results || []);
    });

    // Typeahead: debounced in the search bar, stale requests cancelled by switchMap.
    this.searchTerms.pipe(
      distinctUntilChanged(),
      switchMap(q => q.length > 1 ? this.searchService.liveProductSearch(q) : of([])),
      takeUntilDestroyed(this.destroyRef)
    ).subscribe(results => {
      this.searchResults = results;
      this.cdr.markForCheck();
    });

    this.locationService.currentLocation$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(location => {
      this.currentLocation = location;
      this.cdr.markForCheck();
    });

    this.locationService.deliveryInfo$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(info => {
      this.deliveryInfo = info;
      this.cdr.markForCheck();
    });

    // Keep the header name in sync with profile edits made anywhere in the app.
    this.userService.currentUser$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe(user => {
      if (user?.username) {
        this.userName = user.username;
        this.cdr.markForCheck();
      }
    });

    const storedRadius = this.storageService.getItem('radius');
    if (storedRadius) this.radius = Number(storedRadius);

    if (this.isBrowser) {
      this.searchService.getQuickSuggestions().pipe(takeUntilDestroyed(this.destroyRef)).subscribe(list => {
        this.popularSearches = list.slice(0, 8);
        this.cdr.markForCheck();
      });

      // First-time/guest users with no saved address get prompted to set one -
      // only on shopping pages, not on top of a chat, the account or a policy page.
      setTimeout(() => {
        if (!this.locationService.activeLocation() && needsLocation(this.router.url)) {
          this.openLocationModal();
        }
      }, 600);
    }
  }

  get firstName(): string {
    return (this.userName || '').split(/[\s@]/)[0] || 'Account';
  }

  loginSignup(): void {
    // Guards against a duplicate modal if this click is replayed by Angular's
    // hydration event-replay (withEventReplay) once hydration finishes.
    if (this.modalService.hasOpenModals()) return;
    this.analytics.trackEvent('LOGIN_CLICKED', 'EVENT_TRIGGERED', 1);
    this.modalService.open(AuthComponent, { ariaLabelledBy: 'auth-modal-title', centered: true, size: 'sm' });
  }

  onSearchChange(query: string): void {
    this.searchQuery = query;
    this.searchTerms.next(query.trim());
  }

  onSearch(query: string): void {
    const q = (query ?? this.searchQuery).trim();
    if (!q) return;
    this.analytics.trackSearch(q);
    this.analytics.trackEvent('SEARCH_SUBMITTED', 'SEARCH', 1, `QUERY: ${q}`);
    this.searchResults = [];
    this.router.navigate(['/search'], { queryParams: { q } });
  }

  onSearchCleared(): void {
    this.searchQuery = '';
    this.searchResults = [];
    this.searchTerms.next('');
  }

  selectResult(item: any): void {
    this.router.navigate(['/product', item.slug || item.id]);
    this.searchResults = [];
    this.searchQuery = '';
  }

  toggleProfileDropdown(event: Event): void {
    event.stopPropagation();
    this.showProfileDropdown = !this.showProfileDropdown;
  }

  goToProfileTab(tab: 'profile' | 'orders' | 'wallet' | 'subscriptions' | 'service-bookings'): void {
    this.showProfileDropdown = false;
    this.router.navigate(['/profile'], { queryParams: { tab } });
  }

  logout(): void {
    this.showProfileDropdown = false;
    this.authService.logout();
    this.router.navigate(['/']);
  }

  @HostListener('document:click', ['$event'])
  onClickOutside(event: Event): void {
    const target = event.target as HTMLElement;
    if (!target.closest('.profile-nav')) {
      this.showProfileDropdown = false;
    }
    if (!target.closest('.radius-popover') && !target.closest('.radius-toggle-btn')) {
      this.showRadiusPanel = false;
    }
  }

  @HostListener('document:keydown.escape')
  onEscape(): void {
    this.showProfileDropdown = false;
    this.showRadiusPanel = false;
    this.showCartPreview = false;
  }

  @HostListener('window:scroll')
  onWindowScroll(): void {
    if (!this.isBrowser || this.scrollTicking) return;
    this.scrollTicking = true;
    requestAnimationFrame(() => {
      const scrolled = window.scrollY > 8;
      if (scrolled !== this.isScrolled) {
        this.isScrolled = scrolled;
        this.cdr.markForCheck();
      }
      this.scrollTicking = false;
    });
  }

  goToChats(): void {
    this.showProfileDropdown = false;
    this.router.navigate(['/chats']);
  }

  onCartClick(): void {
    this.showCartPreview = false;
    this.analytics.trackEvent('CART_REDIRECT_CLICKED', 'PAGE_VISIT', 1);
  }

  // Cart hover preview - lazily fetched on hover, desktop only (no hover on touch)
  onCartHoverEnter(): void {
    if (!this.isBrowser || !window.matchMedia('(hover: hover)').matches) return;
    clearTimeout(this.cartPreviewHideTimeout);
    this.showCartPreview = true;

    if (Date.now() - this.cartPreviewFetchedAt < CART_PREVIEW_TTL_MS) return;

    this.cartPreviewLoading = !this.cartPreview;
    this.cartService.getCart().subscribe({
      next: (cart) => {
        this.cartPreview = cart;
        this.cartPreviewLoading = false;
        this.cartPreviewFetchedAt = Date.now();
        this.cdr.markForCheck();
      },
      error: () => {
        this.cartPreview = { items: [], total: 0 };
        this.cartPreviewLoading = false;
        this.cdr.markForCheck();
      },
    });
  }

  onCartHoverLeave(): void {
    this.cartPreviewHideTimeout = setTimeout(() => {
      this.showCartPreview = false;
      this.cdr.markForCheck();
    }, 150);
  }

  // Open the shared address book to select/add a delivery address
  openLocationModal(): void {
    this.locationPicker.open();
  }

  toggleRadiusPanel(event: Event): void {
    event.stopPropagation();
    this.showRadiusPanel = !this.showRadiusPanel;
  }

  onRadiusChange(): void {
    this.storageService.setItem('radius', this.radius.toString());
    this.shopService.setRadius(this.radius);
    // Shop lists re-fetch with the stored lat/lng + new radius (or pincode when there are no coordinates).
    this.shopService.triggerRefresh();
  }
}

/** Chat has its own live channel once a conversation is open; the header badge only needs a slow refresh. */
const UNREAD_POLL_MS = 60000;

const LOCATION_SCOPED_PATHS = ['/category', '/categories', '/products', '/product/', '/search', '/shop/', '/stores', '/cart'];

function needsLocation(url: string): boolean {
  const path = url.split(/[?#]/)[0];
  return path === '/' || LOCATION_SCOPED_PATHS.some(prefix => path.startsWith(prefix));
}
